/** Authenticated fetch wrapper. */

export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api/v1";
const TOKEN_KEY = "rus_ii_token";

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Заголовок авторизации для «сырых» fetch-запросов (multipart upload и т.п.). */
export function authHeaders(): Record<string, string> {
  const token = readToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, text || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Personal read queries bind their request to the current auth context rather
 * than a mutable api.get wrapper from a previous login. Never cache the token. */
export function getForSession<T>(path: string, token: string | null, signal?: AbortSignal): Promise<T> {
  return fetch(`${API_URL}${path}`, {
    credentials: "include", signal,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }).then(handleResponse<T>);
}

/** Preserve HTTP status for mutations that must distinguish a rejected request
 * from an unknown outcome after a lost response. */
export function postForSession<T>(path: string, body: unknown, token: string | null): Promise<T> {
  return fetch(`${API_URL}${path}`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  }).then(handleResponse<T>);
}

function buildHeaders(extra?: HeadersInit): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (extra) {
    if (extra instanceof Headers) {
      extra.forEach((v, k) => {
        headers[k] = v;
      });
    } else if (Array.isArray(extra)) {
      for (const [k, v] of extra) headers[k] = v;
    } else {
      Object.assign(headers, extra as Record<string, string>);
    }
  }
  const token = readToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: buildHeaders(init.headers),
    ...init,
  });
  return handleResponse<T>(res);
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  put: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  del: <T,>(path: string) => request<T>(path, { method: "DELETE" }),
};

/**
 * Скачать файл с защищённого эндпоинта (вложения и т.п.).
 * Обычная ссылка <a href> не подставляет Bearer-токен → 401, поэтому
 * качаем fetch'ем с authHeaders и сохраняем через blob-URL.
 */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, text || res.statusText);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Получить blob-URL файла с защищённого эндпоинта (для превью изображений).
 * URL нужно освобождать через URL.revokeObjectURL.
 */
export async function fetchBlobUrl(path: string): Promise<string> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, text || res.statusText);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
